(ns app.bundle-social-x.platform
  "X (formerly Twitter) accounts through bundle.social: a post with one
   video and the caption as its text.

   Only data and pure functions, see `app.bundle-social.platforms` for
   the keys. The account of the OAuth is the publishing target, no page
   is picked afterwards.

   X charges every post made through its API, and a lot more for a post
   whose text contains a link, so X posts are billed as services of
   their own (`:post-type/service`, `:post-type/link-service`); the
   connect hint explains this before the OAuth.

   For now only videos with a caption; X also takes images, up to four
   media per post and a reply setting
   (https://info.bundle.social/api-reference/platforms/twitter)."
  (:require #?(:clj [app.bundle-social.links :as links])
            [app.bundle-social.text :as text]
            [app.subscription.services :as services]))

(defn icon
  "Font Awesome's x-twitter brand icon."
  [classes]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 512 512"
         :fill "currentColor"
         :aria-hidden "true"
         :class classes}
   [:path {:d "M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"}]])

(def max-weighted-length
  280)

#?(:clj
   (defn weighted-length
     "The length X counts: most CJK characters and emoji count twice, a
      link 23 (twitter-text, X's own library)."
     [s]
     (.-weightedLength (com.twitter.twittertext.TwitterTextParser/parseTweet s))))

#?(:clj
   (defn link-ranges
     "The [start end) character ranges of the links in `s`."
     [s]
     (map (fn [^com.twitter.twittertext.Extractor$Entity entity]
            [(.getStart entity) (.getEnd entity)])
          (.extractURLsWithIndices ^com.twitter.twittertext.Extractor links/extractor ^String s))))

(defn outside-links
  "The `max-length` for `text/shorten` (it keeps `max-length` - 1
   characters before the ellipsis), moved before a link it would cut
   into: a cut link is broken, or worse, a link to another domain
   (https://storrito.co…)."
  [ranges max-length]
  (let [kept (dec max-length)]
    (if-let [start (some (fn [[start end]]
                           (when (< start kept end) start))
                         ranges)]
      (max 1 (inc start))
      max-length)))

(defn fit-weighted
  "`s` shortened until X's weighted length fits, never inside a link.
   The schedule command stores the caption fitted this way (see
   `:post-type/fit-text`). Only clj shortens; in cljs it is the identity
   and the scheduling page's hint counts characters
   (`:post-type/max-text-length`), so its preview of a Japanese caption
   can show more than X gets."
  [s]
  #?(:clj (let [ranges (link-ranges s)]
            (loop [max-length (count s)]
              (let [shortened (text/shorten s max-length)
                    excess (- (weighted-length shortened) max-weighted-length)]
                (if (and (pos? excess) (< 1 max-length))
                  ;; A character weighs at most 2, so cutting excess/2
                  ;; never cuts too much, except when the cut drops a
                  ;; link: it weighed 23, whatever its length. It ends,
                  ;; since max-length decreases on every pass. A cut can
                  ;; leave a prefix X reads as a new link
                  ;; (storrito.coolstuff -> storrito.co…); rare, and
                  ;; billing checks the cut text, as X gets it.
                  (recur (outside-links ranges
                                        (max 1 (- max-length (max 1 (quot excess 2))))))
                  shortened))))
     :cljs s))

(defn post-data
  "The `data.TWITTER` entry of a bundle.social post."
  [{:keys [text upload-id]}]
  (cond-> {:uploadIds [upload-id]}
    (seq text)
    (assoc :text (fit-weighted text))))

(def connect-hint
  "Why X posts cost more, with the prices `app.bundle-social.billing`
   charges, before the OAuth. Never names the provider. Shows the list
   prices, not an organization's price adjustment (like Instagram's
   fallback hint)."
  (let [prices (services/get-service->prices {})]
    {:title "X posts cost more"
     ;; The same words as the service's tooltip on the rates page.
     :body (some #(when (= :x-post (:id %)) (:tooltip %)) (services/get-services {}))
     :points [(str "Each X post costs " (text/format-credits (:x-post prices)) " credits.")
              (str "A post whose text contains a link costs "
                   (text/format-credits (:x-post-link prices))
                   " credits, because X charges a lot more for posts with links. Leave links, including bare domains like example.com, out of the caption to pay the regular price.")]}))

(def platform
  {:social-platform/id "TWITTER"
   :social-platform/key :x
   :social-platform/name "X"
   :social-platform/account-noun "X account"
   :social-platform/icon icon
   :social-platform/text-class ["text-gray-900"]
   :social-platform/pill-class ["bg-gray-50" "border-gray-200" "text-gray-800"]
   :social-platform/launched? true
   :social-platform/connect-card
   {:eyebrow "Video posts"
    :description "Connect an X account for scheduled video posts."
    :button-text "Connect X"
    :style {:card-class ["border-gray-300" "bg-white" "hover:border-gray-400"]
            :eyebrow-class ["bg-gray-100" "text-gray-700" "ring-gray-200"]
            :icon-class ["bg-black" "text-white" "ring-gray-300"]
            :title-class ["text-gray-900"]
            :description-class ["text-gray-600"]
            :button-class ["bg-black" "text-white" "hover:bg-gray-800"]
            :rail-class ["bg-black"]}}
   :social-platform/connect-hint connect-hint
   :social-platform/post-data post-data
   ;; X takes videos up to 140 seconds and 280 characters from accounts
   ;; without Premium, which Storrito cannot tell apart
   ;; (https://info.bundle.social/api-reference/platforms/twitter).
   :social-platform/post-types
   [{:post-type/id "POST"
     :post-type/kind :x-post
     :post-type/label "X post"
     :post-type/group-label "X"
     :post-type/media #{:video}
     :post-type/max-duration-seconds 140
     :post-type/caption? true
     :post-type/max-text-length max-weighted-length
     ;; clj only: in cljs `fit-caption` falls back to the 280
     ;; characters.
     :post-type/fit-text #?(:clj fit-weighted :cljs nil)
     :post-type/service :x-post
     :post-type/link-service :x-post-link
     ;; Legacy subscriptions pay X posts too (Tobias, 2026-10-08).
     :post-type/always-billed? true}]})

(comment
  (weighted-length "…")
  ;; => 2
  (fit-weighted (apply str (repeat 280 "夏")))
  (fit-weighted (str (apply str (repeat 200 "夏")) " storrito.com/sale")))
